// ./src/utils/api/WorkflowService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { WorkflowItem } from "./WorkflowItemService";

// GraphQL queries
const WORKFLOW_LIST_QUERY = gql`
  query WorkflowList($filter: WorkflowFilter, $params: PaginationAndSorting) {
    workflowList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        workflowName
        normalizedWorkflowName
        description
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_GET_QUERY = gql`
  query WorkflowGet($id: ID) {
    workflowGet(id: $id) {
      code
      data {
        id
        accountId
        workflowName
        normalizedWorkflowName
        description
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_GET_MANY_QUERY = gql`
  query WorkflowGetMany($ids: [ID]) {
    workflowGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        workflowName
        description
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const WORKFLOW_CREATE_MUTATION = gql`
  mutation WorkflowCreate($params: WorkflowInput) {
    workflowCreate(params: $params) {
      code
      data {
        id
        accountId
        workflowName
        description
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_UPDATE_MUTATION = gql`
  mutation WorkflowUpdate($where: WorkflowWhereInput, $params: WorkflowInput) {
    workflowUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        workflowName
        description
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_REMOVE_MUTATION = gql`
  mutation WorkflowRemove($where: WorkflowWhereInput) {
    workflowRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_REMOVE_MANY_MUTATION = gql`
  mutation WorkflowRemoveMany($where: [WorkflowWhereInput]) {
    workflowRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Workflow {
  id?: string | undefined;
  accountId?: string | undefined;
  workflowName?: string | undefined;
  description?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  workflowItems?: WorkflowItem[];
  rights?: string[];
}

export interface WorkflowInput {
  id?: string | null | undefined;
  workflowName?: string | null | undefined;
  description?: string | null | undefined;
  status?: number | null | undefined;
}

export interface WorkflowFilter {
  id?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface WorkflowWhereInput {
  id?: string | undefined;
}

/**
 * Workflow service that handles all workflow-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class WorkflowService {
  /**
   * Get a list of workflows with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: WorkflowFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single workflow by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple workflows by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new workflow
   */
  static async create(params: WorkflowInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(WORKFLOW_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing workflow
   */
  static async update(
    where: WorkflowWhereInput,
    params: WorkflowInput,
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(WORKFLOW_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a workflow
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(WORKFLOW_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple workflows
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      WORKFLOW_REMOVE_MANY_MUTATION,
      {
        where,
      },
    );
    return result;
  }
}
